import React, { useState, useContext, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Modal } from "react-bootstrap"
import { faTrash } from "@fortawesome/pro-regular-svg-icons"
import { faTrash as faTrashSolid } from "@fortawesome/free-solid-svg-icons"
import FileUploadButton from "../user-files/FileUploadButton"
import FileDisplayer from "../user-files/FileDisplayer"
import { FirebaseContext } from "../Firebase"
import useUserFiles from "../user-files/useUserFiles"
import { faChevronRight, faChevronLeft } from "@fortawesome/pro-regular-svg-icons"
import DeleteButton from "./shared/components/DeleteButton"
import "./CurriculumImageUploadModal.css"

/**
 * Renders a modal that allows a user to upload an image, select an image they
 * already uploaded to the interactive curriculum, or select from a pre-made
 * library of interactive curriculum images
 * @param {boolean} props.show - Whether the modal should be visible
 * @param {Function} props.onClose - Runs when the modal closes
 * @param {Function} props.updateImageURL - Given a new image URL, updates saved image URL
 * @param {string} props.title - Title of the modal
 * @param {boolean} props.isNew - Whether the submit button should say "update" or "add" image
 * @returns {JSX.Element}
 */
const CurriculumImageUploadModal = ({show, onClose, updateImageURL, title, isNew}) => {
  const { firebase } = useContext(FirebaseContext)
  const [selectedTab, setSelectedTab] = useState("USER")
  const [selectedFileId, setSelectedFileId] = useState("")
  const [previousFileIDs, setPreviousFileIDs] = useState([])
  const [prevFilePageNum, setPrevFilePageNum] = useState(1)
  const [libraryImages, setLibraryImages] = useState([])
  const [libPageNum, setLibPageNum] = useState(1)

  useEffect(() => {
    const fetchLibraryImages = async () => {
      const libImageFiles = await firebase.fetchCurriculumImageLibrary()
      setLibraryImages(libImageFiles)
    }
    fetchLibraryImages()
  }, [firebase])

  useEffect(() => {
    const fetchPreviouslyUploadedImages = async () => {
      const prevFileIDs = await firebase.fetchCurriculumPreviousImages()
      setPreviousFileIDs(prevFileIDs)
    }
    fetchPreviouslyUploadedImages()
  }, [firebase])

  const {filesList, addFile} = useUserFiles(null, "curriculum-hosted-images", previousFileIDs)

  const processNewFile = async (files) => {
    const newFile = files[0]
    const fileId = addFile(newFile, true)
    setSelectedFileId(fileId)
    setPreviousFileIDs([fileId, ...previousFileIDs])
  }

  const handleFileDelete = async (file) => {
    if (file.isLoading) {
      window.alert("Please wait for your image to finish loading")
      return
    }
    if (window.confirm("Warning: Deleting this image will remove it from any modules it's been used in.")) {
      if (selectedFileId === file.fileId) {
        setSelectedFileId("")
      }
      setPreviousFileIDs(previousFileIDs.filter(id => id !== file.fileId))
      await firebase.deleteUserFile("curriculum-hosted-images", file.fileId)
    }
  }

  const handleTabSwitch = (tabName) => {
    if (tabName !== selectedTab) {
      setSelectedTab(tabName)
    }
  }

  const handleClose = () => {
    onClose()
    setSelectedTab("USER")
    setSelectedFileId("")
    setPrevFilePageNum(1)
    setLibPageNum(1)
  }

  const onSave = async () => {
    if (!selectedFileId) {
      window.alert("Please select an image")
      return
    }
    const file = [...filesList, ...libraryImages].find(f => f.fileId === selectedFileId)
    if (!file || !file.fileURL) {
      window.alert("Please wait for your image to finish loading")
      return
    }
    updateImageURL(file.fileURL);
    handleClose();
  }

  const renderPaginationSection = (currentPage, pageSize, totalPages, totalItems, setPageNumber) => {
    return (
      <div className="pagination">
        <button 
          onClick={() => setPageNumber(currentPage - 1)} 
          disabled={currentPage === 1}
        >
          <FontAwesomeIcon icon={faChevronLeft} color="#406C70" />
        </button>
        <span>
          {((currentPage - 1) * pageSize) + 1} - {Math.min(totalItems, currentPage * pageSize)} of {totalItems}
        </span>
        <button 
          onClick={() => setPageNumber(currentPage + 1)} 
          disabled={currentPage === totalPages}
        >
          <FontAwesomeIcon icon={faChevronRight} color="#406C70" />
        </button>
      </div>
    )
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <h4 style={{marginBottom: "0px"}}>{title}</h4>
      </Modal.Header>
      <Modal.Body>
        <div className="curriculum-image-selector">
          <div className="tabs-container">
            <button 
              className={`tab left-tab ${selectedTab === "USER" ? "active-tab" : "inactive-tab"}`}
              onClick={() => handleTabSwitch("USER")}
            >
              My Images
            </button>
            <button 
              className={`tab right-tab ${selectedTab === "LIBRARY" ? "active-tab" : "inactive-tab"}`}
              onClick={() => handleTabSwitch("LIBRARY")}
            >
              Image Library
            </button>
          </div>

          {(selectedTab === "USER") && (
            <>
            <div className="upload-button-container">
              <FileUploadButton
                instructions="Click to upload image or drop file from your computer"
                processFiles={processNewFile}
                allowedTypes={["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml", "image/apng"]}
                id="image-component-creator-modal"
              />
            </div>
            {filesList && filesList.length > 0 && (
              <>
              <div className="uploaded-images-label">Uploaded Images</div>
              <div className="image-component-thumbnails-container user-image-pages">
                {filesList.slice((prevFilePageNum - 1) * 5, prevFilePageNum * 5).map(imageFile => (
                  <div className="user-image-thumbnail-container" key={imageFile.fileId} >
                    <button 
                      className={`image-component-thumbnail ${selectedFileId === imageFile.fileId ? "selected-image" : ""}`}
                      onClick={() => setSelectedFileId(imageFile.fileId)}
                    >
                      <FileDisplayer
                        file={imageFile}
                        isThumbnail={true}
                      />
                    </button>
                    <DeleteButton onClick={() => handleFileDelete(imageFile)} className="user-image-delete-button" />
                  </div>
                ))}
              </div>
              {previousFileIDs.length > 5 && (
                renderPaginationSection(prevFilePageNum, 5, Math.floor(((previousFileIDs.length - 1) / 5) + 1), previousFileIDs.length, setPrevFilePageNum)
              )}
              </>
            )}
            </>
          )}
            
          {(selectedTab === "LIBRARY") && (
            <>
            <div className="image-component-thumbnails-container library-pages">
              {libraryImages.slice((libPageNum - 1) * 10, libPageNum * 10).map(imageFile => (
                <button 
                  key={imageFile.fileURL} 
                  className={`image-component-thumbnail ${selectedFileId === imageFile.fileId ? "selected-image" : ""}`}
                  onClick={() => setSelectedFileId(imageFile.fileId)}
                >
                  <FileDisplayer
                    file={imageFile}
                    isThumbnail={true}
                  />
                </button>
              ))}
            </div>
            {renderPaginationSection(libPageNum, 10, Math.floor(((libraryImages.length - 1) / 10) + 1), libraryImages.length, setLibPageNum)}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          onClick={onSave}
          className="button component-button component-button-small component-button-default"
        >
          {isNew ? "Add Image" : "Update Image"}
        </button>
      </Modal.Footer>
    </Modal>
  )
  
}

export default CurriculumImageUploadModal
